import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackCustomEvent } from "@/lib/metaPixel";
import { devLog } from "@/lib/devLog";

// Business WhatsApp destination. Kept as a constant (not env-driven) so the
// number is bundled into the deployed HTML — useful for social/link previews
// and for search engines that inspect anchor destinations.
const WHATSAPP_NUMBER = "447520656967"; // +44 7520 656967 (Danny Clans Media)

// Routes where the float should stay hidden — admin surfaces are internal
// tools, not lead-capture surfaces.
const HIDDEN_ROUTE_PATTERNS = [/^\/admin/, /^\/admin-clan/];

const buildWhatsAppUrl = (message) => {
  const encoded = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encoded}`;
};

/**
 * Floating WhatsApp CTA anchored to the bottom-right of the viewport.
 *
 * • Uses the official WhatsApp brand green (#25D366) with a subtle idle
 *   pulse ring to draw attention without being obtrusive.
 * • Shows an expanded label on desktop hover and on mobile after a short
 *   idle delay, then collapses back to the icon-only pill.
 * • Fires a Meta Pixel custom event on click for CAPI attribution.
 * • Fully keyboard-accessible + honours prefers-reduced-motion.
 */
export const WhatsAppFloat = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);

  // Determine visibility per-route. We recompute on every path change so
  // admin surfaces stay untouched.
  useEffect(() => {
    const hidden = HIDDEN_ROUTE_PATTERNS.some((re) => re.test(location.pathname));
    setVisible(!hidden);
  }, [location.pathname]);

  // Show a one-time expanded nudge ~2.5 s after mount so first-time visitors
  // notice the entry point without being ambushed on paint.
  useEffect(() => {
    if (!visible) return;
    const reducedMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) return;
    const expandTimer = setTimeout(() => setExpanded(true), 2500);
    const collapseTimer = setTimeout(() => setExpanded(false), 7500);
    return () => {
      clearTimeout(expandTimer);
      clearTimeout(collapseTimer);
    };
  }, [visible]);

  if (!visible) return null;

  const label = t("common.whatsappCta", "Chat on WhatsApp");
  const message = t(
    "common.whatsappPrefill",
    "Hi Danny Clans, I'd like to learn more about DC Dashboard and agency ad-account access."
  );

  const handleClick = () => {
    // Fire-and-forget analytics ping. Never block the outbound nav.
    // Fires a Meta Pixel custom event (`WhatsAppClick`) that shows up
    // as its own row in Events Manager and is mirrored via CAPI for
    // iOS 14+ / cookieless attribution. `trackCustomEvent` handles its
    // own network/SDK failures via devLog; this outer catch guards
    // against import-time or synchronous throws leaking out and
    // breaking the anchor click.
    try {
      trackCustomEvent("WhatsAppClick", {
        source: "float_button",
        path: location.pathname,
      });
    } catch (err) {
      devLog.warn("WhatsAppFloat: click tracker threw", err);
    }
  };

  return (
    <a
      href={buildWhatsAppUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
      aria-label={label}
      data-testid="whatsapp-float-button"
      className="fixed bottom-5 right-5 z-[300] flex items-center gap-2 rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_-8px_rgba(37,211,102,0.55)] ring-1 ring-black/5 transition-[padding,width,transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-[0_16px_40px_-10px_rgba(37,211,102,0.65)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/40 sm:bottom-6 sm:right-6"
      style={{ padding: expanded ? "12px 20px 12px 14px" : "14px" }}
    >
      {/* Idle pulse ring — pauses on hover so the label reads cleanly. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 rounded-full ring-2 ring-[#25D366]/70 ${expanded ? "" : "animate-ping"}`}
        style={{ animationDuration: "2.4s" }}
      />
      {/* WhatsApp glyph — inline SVG to avoid an extra icon dependency. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 32 32"
        width="28"
        height="28"
        className="relative shrink-0"
        fill="currentColor"
      >
        <path d="M19.11 17.36c-.28-.14-1.66-.82-1.92-.91-.26-.1-.45-.14-.63.14-.19.28-.72.91-.88 1.09-.16.19-.32.21-.6.07-.28-.14-1.18-.44-2.25-1.4-.83-.75-1.39-1.66-1.55-1.94-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.08-.23-.55-.46-.48-.63-.49l-.54-.01c-.19 0-.49.07-.75.35-.26.28-.99.97-.99 2.36 0 1.39 1.01 2.74 1.15 2.93.14.19 1.99 3.04 4.83 4.26.68.29 1.2.46 1.61.59.68.22 1.29.19 1.78.12.54-.08 1.66-.68 1.9-1.34.24-.65.24-1.21.16-1.34-.07-.13-.26-.21-.54-.35zM16.03 4.5C9.68 4.5 4.5 9.65 4.5 15.99c0 2.03.53 4.02 1.55 5.78L4.5 27.5l5.87-1.53c1.71.93 3.64 1.44 5.66 1.44 6.35 0 11.5-5.15 11.5-11.49S22.38 4.5 16.03 4.5zm0 20.98c-1.83 0-3.62-.49-5.19-1.42l-.37-.22-3.5.91.94-3.41-.24-.39a9.44 9.44 0 01-1.44-5.02c0-5.22 4.25-9.47 9.5-9.47s9.5 4.25 9.5 9.47c0 5.22-4.25 9.55-9.2 9.55z" />
      </svg>
      <span
        className={`overflow-hidden whitespace-nowrap text-sm font-semibold transition-[max-width,opacity,margin] duration-200 ${
          expanded ? "max-w-[220px] opacity-100 ml-1" : "max-w-0 opacity-0 ml-0"
        }`}
      >
        {label}
      </span>
    </a>
  );
};

export default WhatsAppFloat;
